import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  Check,
  ChevronDown,
  CircleDollarSign,
  LayoutTemplate,
  MessageSquareText,
  Settings2,
  ShieldCheck,
  Sparkles,
  Star,
  Workflow,
  X,
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const viewport = { once: true, amount: 0.2 };

const problemPoints = [
  'Vous répondez trop tard aux messages',
  'Vos prix ne sont pas optimisés',
  'Votre calendrier est mal synchronisé',
  'Votre annonce ne convertit pas assez',
  'Vous passez trop de temps à gérer',
];

const transformationPoints = [
  "Un système professionnel comme les conciergeries",
  "Des réponses pro en 1 clic aux clients grâce à l'IA",
  'Des notifications automatiques aux voyageurs',
  'Un pricing intelligent piloté automatiquement',
  'Une organisation claire et sans stress',
];

const modules = [
  {
    icon: Workflow,
    title: 'Mise en place du système',
    items: ['Channel manager', 'Synchronisation calendriers', 'Connexion Airbnb / Booking'],
  },
  {
    icon: LayoutTemplate,
    title: 'Optimisation annonce',
    items: ['Template description', 'Structure SEO', 'Conseils photos'],
  },
  {
    icon: MessageSquareText,
    title: 'Automatisation des messages',
    items: ['Messages automatiques', "Scripts prêts à l'emploi"],
  },
  {
    icon: CircleDollarSign,
    title: 'Pricing dynamique',
    items: ['Configuration', 'Stratégie tarifaire'],
  },
  {
    icon: ShieldCheck,
    title: 'Documents & process',
    items: ['Contrats', 'Messages clients', 'Checklists', "Template modifiable livret d'accueil"],
  },
  {
    icon: Settings2,
    title: 'Formation + accompagnement',
    items: ['Vidéos', 'Communauté', 'Lives', 'Coaching en visio'],
  },
];

const bonuses = [
  "Accès espace d'entraide privé 6 mois",
  'Mises à jour du système',
  'Support groupe',
];

const faqItems = [
  {
    question: 'Est-ce compliqué ?',
    answer: (
      <div className="space-y-4">
        <p>
          Non. L’accompagnement est justement conçu pour rendre la mise en place du système claire, progressive et accessible.
        </p>
        <p>
          Vous avancez <strong>étape par étape</strong>, avec un cadre précis sur ce qu’il faut mettre en place, dans quel ordre, et avec quels outils.
        </p>
        <p>
          L’objectif n’est pas de vous noyer dans la technique, mais de vous permettre de structurer votre activité de manière simple, logique et efficace.
        </p>
      </div>
    ),
  },
  {
    question: 'Est-ce que je dois être technique ?',
    answer: (
      <div className="space-y-4">
        <p>
          Non. Vous n’avez pas besoin d’être expert en outils digitaux, en automatisation ou en configuration avancée.
        </p>
        <p>
          Le système a été pensé pour des propriétaires et des gestionnaires qui veulent un fonctionnement professionnel, sans devoir devenir techniciens.
        </p>
        <p>
          Vous savez <strong>quoi faire</strong>, <strong>dans quel ordre</strong>, et <strong>avec quels outils</strong>, avec une méthode simplifiée et orientée résultats.
        </p>
      </div>
    ),
  },
  {
    question: 'Est-ce que ça fonctionne partout ?',
    answer: (
      <div className="space-y-4">
        <p>
          Oui. Le système est conçu pour être <strong>adaptable à tout type de bien</strong> et à la majorité des marchés de location saisonnière.
        </p>
        <p>
          Que vous gériez une villa, un appartement, un chalet ou plusieurs logements, la logique de pilotage reste la même : centraliser, automatiser, optimiser et structurer.
        </p>
        <p>
          Ensuite, certains réglages sont naturellement ajustés selon votre bien, vos plateformes de diffusion et votre façon actuelle de fonctionner.
        </p>
      </div>
    ),
  },
  {
    question: "Combien coûte l’accompagnement ?",
    answer: (
      <div className="space-y-4">
        <p>
          Nous proposons actuellement <strong>deux formats</strong>, selon votre profil et vos objectifs :
        </p>
        <div className="space-y-2">
          <p>Un accompagnement pour les propriétaires qui souhaitent mettre en place leur système et devenir autonomes dans la gestion de leur location.</p>
          <p>Un accompagnement plus avancé, destiné aux gestionnaires ou conciergeries, incluant notamment la mise en place de systèmes d’acquisition de propriétaires.</p>
        </div>
        <p>
          <strong>Fonctionnement de l’accompagnement :</strong> l’accompagnement se fait sous la forme d’un paiement unique, vous donnant accès au programme complet ainsi qu’à l’ensemble des ressources et bonus inclus.
        </p>
        <p>
          <strong>Concernant les outils utilisés :</strong> la mise en place du système nécessite l’utilisation d’outils professionnels comme un channel manager, le paiement ou l’automatisation.
        </p>
        <div className="space-y-2">
          <p>Ces outils sont externes à DelegAI, directement souscrits à votre nom, et restent à votre charge.</p>
          <p>Exemples : Lodgify, Stripe, Swikly, etc.</p>
        </div>
        <p>
          Nous sélectionnons uniquement des outils <strong>fiables</strong>, <strong>reconnus dans le secteur</strong>, avec un excellent <strong>rapport qualité / performance</strong>.
        </p>
        <p>
          Ce sont les mêmes solutions utilisées aujourd’hui par des conciergeries et des établissements professionnels.
        </p>
        <p>
          <strong>L’objectif :</strong> vous rendre 100% autonome, avec un système qui vous appartient réellement, sans dépendre d’un prestataire.
        </p>
      </div>
    ),
  },
];

const situationOptions = ['Airbnb', 'Booking', 'Google Rental', 'Autre'];

const initialFormData = {
  nom: '',
  prenom: '',
  pays: '',
  telephone: '',
  email: '',
  profil: '',
  typeBien: '',
  lienAnnonce: '',
  situationActuelle: [],
  autreSituation: '',
  utiliseChannelManager: '',
  channelManagerNom: '',
  pretSuivreMethode: '',
  acceptationLegale: false,
};

const validateForm = (formData) => {
  if (!formData.nom.trim()) return 'Le nom est requis.';
  if (!formData.prenom.trim()) return 'Le prénom est requis.';
  if (!formData.pays.trim()) return 'Le pays est requis.';
  if (!formData.telephone.trim()) return 'Le numéro de téléphone est requis.';
  if (!formData.email.trim()) return "L'email est requis.";
  if (!formData.profil) return 'Le profil est requis.';
  if (!formData.typeBien.trim()) return 'Le type de bien est requis.';
  if (!formData.utiliseChannelManager) return "Veuillez préciser si vous utilisez un channel manager.";
  if (formData.utiliseChannelManager === 'oui' && !formData.channelManagerNom.trim()) {
    return 'Veuillez préciser le nom du channel manager.';
  }
  if (!formData.pretSuivreMethode) {
    return 'Veuillez préciser si vous êtes prêt à suivre une méthode.';
  }
  if (!formData.acceptationLegale) {
    return 'Vous devez accepter la politique de confidentialité et les CGV.';
  }
  if (formData.situationActuelle.includes('Autre') && !formData.autreSituation.trim()) {
    return 'Veuillez préciser votre situation actuelle.';
  }
  return '';
};

const SectionTitle = ({ eyebrow, title, description, centered = true }) => (
  <div className={centered ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}>
    {eyebrow ? (
      <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-blue-700">
        <Sparkles className="h-3.5 w-3.5" />
        {eyebrow}
      </div>
    ) : null}
    <h2 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">{title}</h2>
    {description ? (
      <p className="mt-4 text-lg leading-8 text-slate-600">{description}</p>
    ) : null}
  </div>
);

function SystemeLocationPerformancePage() {
  const [formData, setFormData] = useState(initialFormData);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const referralSlotRef = useRef(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Système Location Performance | Deleg-ai';

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const created = !robotsMeta;

    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }

    const previousRobots = robotsMeta.getAttribute('content');
    robotsMeta.setAttribute('content', 'noindex,nofollow');

    return () => {
      document.title = previousTitle;
      if (created) {
        robotsMeta.remove();
      } else if (previousRobots) {
        robotsMeta.setAttribute('content', previousRobots);
      } else {
        robotsMeta.removeAttribute('content');
      }
    };
  }, []);

  useEffect(() => {
    if (!isFormOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFormOpen]);

  useEffect(() => {
    const slot = referralSlotRef.current;

    if (!slot) {
      return undefined;
    }

    let movedElement = null;
    let originalParent = null;
    let originalNextSibling = null;
    let originalStyle = null;

    const findReferralWidget = () => {
      const interactiveElements = Array.from(document.querySelectorAll('button, a, [role="button"]'));

      return interactiveElements.find((element) =>
        element.textContent?.toLowerCase().includes('recommander deleg.ai'),
      );
    };

    const moveReferralWidgetToFooter = () => {
      if (movedElement || !slot.isConnected) {
        return;
      }

      const widget = findReferralWidget();

      if (!widget) {
        return;
      }

      movedElement = widget;
      originalParent = widget.parentNode;
      originalNextSibling = widget.nextSibling;
      originalStyle = widget.getAttribute('style');

      slot.appendChild(widget);
      widget.style.position = 'static';
      widget.style.inset = 'auto';
      widget.style.left = 'auto';
      widget.style.right = 'auto';
      widget.style.top = 'auto';
      widget.style.bottom = 'auto';
      widget.style.margin = '0';
      widget.style.zIndex = 'auto';
    };

    moveReferralWidgetToFooter();

    const observer = new MutationObserver(() => {
      moveReferralWidgetToFooter();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();

      if (!movedElement || !originalParent) {
        return;
      }

      if (originalStyle === null) {
        movedElement.removeAttribute('style');
      } else {
        movedElement.setAttribute('style', originalStyle);
      }

      if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
        originalParent.insertBefore(movedElement, originalNextSibling);
      } else {
        originalParent.appendChild(movedElement);
      }
    };
  }, []);

  const openFormModal = () => {
    setStatus({ type: '', message: '' });
    setIsFormOpen(true);
  };

  const closeFormModal = () => {
    if (isSubmitting) {
      return;
    }
    setIsFormOpen(false);
  };

  const handleChange = ({ target }) => {
    const { name, value, type, checked } = target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSituationChange = ({ target }) => {
    const { value, checked } = target;
    setFormData((current) => ({
      ...current,
      situationActuelle: checked
        ? [...current.situationActuelle, value]
        : current.situationActuelle.filter((item) => item !== value),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: '', message: '' });

    const validationError = validateForm(formData);
    if (validationError) {
      setStatus({ type: 'error', message: validationError });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      nom: formData.nom,
      prenom: formData.prenom,
      pays: formData.pays,
      telephone: formData.telephone,
      email: formData.email,
      profil: formData.profil,
      type_de_bien: formData.typeBien,
      lien_annonce: formData.lienAnnonce,
      situation_actuelle: formData.situationActuelle,
      autre_situation: formData.autreSituation,
      utilise_channel_manager: formData.utiliseChannelManager,
      channel_manager_nom: formData.channelManagerNom,
      pret_a_suivre_une_methode: formData.pretSuivreMethode,
      acceptation_legale: formData.acceptationLegale,
      source_page: 'systeme-location-performance',
      submitted_at: new Date().toISOString(),
    };

    try {
      const response = await fetch('https://hook.eu1.make.com/p94sncbpf3ld5nwwm3aav6vyw8jhrr41', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Webhook request failed');
      }

      setStatus({
        type: 'success',
        message: 'Votre candidature a bien été envoyée. Nous revenons vers vous rapidement.',
      });
      setFormData(initialFormData);
    } catch (error) {
      setStatus({
        type: 'error',
        message: "L'envoi a échoué. Réessayez dans un instant.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Poppins',sans-serif] text-slate-900">
      <div className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-slate-950/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 text-white shadow-lg shadow-blue-950/30">
              <Star className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.3em] text-slate-400">Deleg-ai</p>
              <p className="text-sm font-semibold text-white">Système Location Performance™</p>
            </div>
          </div>
          <button
            type="button"
            onClick={openFormModal}
            className="hidden rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:bg-blue-50 md:inline-flex"
          >
            Postuler pour l'accompagnement
          </button>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-950 px-6 pb-20 pt-32 text-white lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.35),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(139,92,246,0.28),_transparent_24%),linear-gradient(180deg,_rgba(15,23,42,0.96),_rgba(2,6,23,1))]" />
        <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:72px_72px]" />

        <div className="relative mx-auto grid max-w-7xl gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeInUp}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-blue-100 shadow-lg shadow-blue-950/20">
              <Bot className="h-4 w-4" />
              Offre digitale pour propriétaires autonomes et rentables
            </div>
            <h1 className="max-w-4xl text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Transformez votre location en machine à réservations
              <span className="block bg-gradient-to-r from-blue-300 via-white to-violet-300 bg-clip-text text-transparent">
                sans y passer vos soirées
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
              Mettez en place un système complet avec calendrier synchronisé, prix intelligents, messages automatiques et réponses semi-automatiques.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={openFormModal}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-7 py-4 text-base font-semibold text-white shadow-xl shadow-blue-950/30 transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-900/30"
              >
                Postuler pour l'accompagnement
                <ArrowRight className="h-5 w-5" />
              </button>
              <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm text-slate-300">
                <Check className="h-4 w-4 text-blue-300" />
                Sans prestataire, sans commissions, avec une méthode claire
              </div>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-3">
              {['Calendrier centralisé', 'Pricing piloté', 'Réponses plus rapides'].map((item) => (
                <div
                  key={item}
                  className="rounded-3xl border border-white/10 bg-white/5 px-5 py-4 text-sm text-slate-200 shadow-lg shadow-slate-950/10 transition duration-300 hover:-translate-y-1 hover:border-blue-300/30 hover:bg-white/10"
                >
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeInUp}
            transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            className="relative"
          >
            <div className="absolute -left-10 top-10 h-28 w-28 rounded-full bg-blue-500/20 blur-3xl" />
            <div className="absolute -right-6 bottom-8 h-28 w-28 rounded-full bg-violet-500/20 blur-3xl" />
            <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/10 p-6 shadow-2xl shadow-slate-950/40 backdrop-blur">
              <div className="rounded-[28px] border border-white/10 bg-slate-900/90 p-6">
                <div className="flex items-center justify-between border-b border-white/10 pb-5">
                  <div>
                    <p className="text-sm text-slate-400">Tableau de pilotage</p>
                    <p className="mt-1 text-xl font-semibold text-white">Location optimisée</p>
                  </div>
                  <div className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-300">
                    En place
                  </div>
                </div>

                <div className="space-y-4 py-6">
                  {[
                    ['Synchronisation Airbnb / Booking', 'Active'],
                    ['Messages voyageurs semi-automatiques', 'Prêt'],
                    ['Tarification dynamique', 'Optimisée'],
                    ['Checklist opérationnelle', 'Structurée'],
                  ].map(([label, status]) => (
                    <div
                      key={label}
                      className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-4 transition duration-300 hover:border-blue-300/20 hover:bg-white/10"
                    >
                      <span className="text-sm text-slate-200">{label}</span>
                      <span className="rounded-full bg-blue-500/15 px-3 py-1 text-xs font-medium text-blue-200">{status}</span>
                    </div>
                  ))}
                </div>

                <div className="grid gap-4 border-t border-white/10 pt-5 sm:grid-cols-2">
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-sm text-slate-400">Temps gagné</p>
                    <p className="mt-2 text-2xl font-semibold text-white">Gestion allégée</p>
                  </div>
                  <div className="rounded-2xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 p-4">
                    <p className="text-sm text-blue-100/80">Objectif</p>
                    <p className="mt-2 text-2xl font-semibold text-white">Plus de réservations</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="bg-white px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-7xl"
        >
          <SectionTitle
            eyebrow="Le problème"
            title="Vous perdez des réservations sans le savoir"
            description="Quand les outils ne sont pas reliés, que les réponses prennent du retard et que la stratégie tarifaire reste floue, la performance baisse sans signal évident."
          />

          <div className="mt-10 overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-xl shadow-slate-200/60">
            <img
              src="/images/systeme-location-performance/problem-illustration.svg"
              alt="Illustration des réservations perdues, retards de réponse et optimisation manquante"
              className="h-56 w-full object-cover sm:h-72"
              loading="lazy"
            />
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-5">
            {problemPoints.map((item, index) => (
              <div
                key={item}
                className="group rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-xl hover:shadow-slate-200/70"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-sm font-semibold text-white transition duration-300 group-hover:bg-gradient-to-br group-hover:from-blue-500 group-hover:to-violet-500">
                  0{index + 1}
                </div>
                <p className="text-base font-medium leading-7 text-slate-800">{item}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* TRANSFORMATION SECTION */}
      <section className="bg-slate-50 px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start"
        >
          <div className="max-w-xl">
            <SectionTitle
              eyebrow="La transformation"
              title="Voici ce que vous allez mettre en place"
              description="Un cadre opérationnel clair, inspiré des meilleures pratiques de gestion locative, mais pensé pour un propriétaire qui veut garder la main."
              centered={false}
            />

            <div className="mt-8 overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-lg shadow-slate-200/60">
              <img
                src="/images/systeme-location-performance/hero-illustration.svg"
                alt="Illustration d'un tableau de pilotage pour location saisonniere"
                className="h-56 w-full object-cover sm:h-72"
                loading="lazy"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {transformationPoints.map((item) => (
              <div
                key={item}
                className="group flex gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl hover:shadow-slate-200/60"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition duration-300 group-hover:bg-gradient-to-br group-hover:from-blue-500 group-hover:to-violet-500 group-hover:text-white">
                  <Check className="h-5 w-5" />
                </div>
                <p className="text-base leading-7 text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* OFFER INTRO SECTION */}
      <section className="bg-white px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-5xl rounded-[32px] border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-8 py-12 text-white shadow-2xl shadow-slate-300/40 sm:px-12"
        >
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-blue-100">
                Offre
              </div>
              <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Système Location Rentable™</h2>
              <p className="mt-4 text-lg leading-8 text-slate-300">
                Un accompagnement complet pour transformer votre location en système rentable, même si vous n’êtes pas expert.
              </p>
            </div>
            <div className="rounded-3xl border border-white/10 bg-white/10 px-6 py-5 backdrop-blur">
              <p className="text-sm uppercase tracking-[0.22em] text-slate-300">Pensé pour</p>
              <p className="mt-2 text-xl font-semibold text-white">Tout type de logement en location saisonnière</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* MODULES SECTION */}
      <section className="bg-slate-50 px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-7xl"
        >
          <SectionTitle
            eyebrow="Contenu de l'offre"
            title="Tout ce qu’il faut pour construire votre système sans dépendre d’un prestataire"
            description="Chaque module répond à un bloc concret de votre activité pour vous permettre de mettre en place un fonctionnement autonome, cohérent et évolutif."
          />

          <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <div
                  key={module.title}
                  className="group rounded-[28px] border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1.5 hover:border-blue-200 hover:shadow-2xl hover:shadow-slate-200/70"
                >
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-white transition duration-300 group-hover:bg-gradient-to-br group-hover:from-blue-500 group-hover:to-violet-500">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-950">{module.title}</h3>
                  <ul className="mt-5 space-y-3">
                    {module.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                        <Check className="mt-1 h-4 w-4 shrink-0 text-blue-600" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* BONUS SECTION */}
      <section className="bg-white px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-6xl rounded-[32px] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-violet-50 p-8 shadow-xl shadow-slate-200/60 sm:p-10"
        >
          <SectionTitle
            eyebrow="Bonus"
            title="Bonus inclus"
            description="Vous ne repartez pas seulement avec une méthode. Vous bénéficiez aussi d’un environnement pour garder votre système à jour et avancer plus vite."
          />

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {bonuses.map((item) => (
              <div
                key={item}
                className="rounded-3xl border border-white bg-white/80 p-6 shadow-sm backdrop-blur transition duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 text-white">
                  <Sparkles className="h-5 w-5" />
                </div>
                <p className="text-base font-medium leading-7 text-slate-800">{item}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 overflow-hidden rounded-[30px] border border-blue-100 bg-white shadow-xl shadow-slate-200/60">
            <img
              src="/images/systeme-location-performance/support-illustration.svg"
              alt="Illustration de l'accompagnement et du support continu"
              className="h-56 w-full object-cover sm:h-72"
              loading="lazy"
            />
          </div>
        </motion.div>
      </section>

      {/* DIFFERENTIATION SECTION */}
      <section className="bg-slate-50 px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-5xl text-center"
        >
          <SectionTitle eyebrow="Différenciation" title="Pourquoi ce système fonctionne" />
          <p className="mt-8 text-xl leading-9 text-slate-700">
            Parce qu’il est basé sur des systèmes déjà utilisés sur des dizaines de biens et optimisés en conditions réelles. Créé par un expert du digital, ancien gérant de conciergerie et fondateur de deleg-ai et de pulsia.ai.
          </p>
        </motion.div>
      </section>

      {/* APPLICATION SECTION */}
      <section id="candidature" className="bg-white px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-6xl"
        >
          <div className="overflow-hidden rounded-[36px] border border-slate-200 bg-slate-950 text-white shadow-2xl shadow-slate-300/50">
            <div className="bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.35),_transparent_35%),radial-gradient(circle_at_80%_20%,_rgba(139,92,246,0.35),_transparent_30%)] px-8 py-12 sm:px-12">
              <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
                <div>
                  <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-blue-100">
                    Candidature
                  </div>
                  <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Postulez pour l'accompagnement</h2>
                  <p className="mt-4 text-lg leading-8 text-slate-300">
                    Cette offre s'adresse aux propriétaires et aux sociétés de conciergerie qui veulent structurer un système performant sans dépendre d'un prestataire.
                  </p>

                  <div className="mt-8 rounded-[28px] border border-white/10 bg-white/10 p-6 backdrop-blur">
                    <p className="text-sm leading-7 text-slate-300">
                      Le formulaire de candidature s'ouvre en popup pour vous laisser parcourir la page librement, puis postuler en quelques minutes quand vous etes pret.
                    </p>
                    <button
                      type="button"
                      onClick={openFormModal}
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:bg-blue-50"
                    >
                      Ouvrir le formulaire
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-8 space-y-3 text-sm text-slate-300">
                    <div className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                      <span>Analyse de votre situation actuelle et de vos outils</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                      <span>Vérification de votre capacité à appliquer une méthode complète</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                      <span>Retour rapide après envoi de votre candidature</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-[28px] border border-white/10 bg-white/10 p-6 backdrop-blur">
                  <h3 className="text-xl font-semibold text-white">Candidature en popup</h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300">
                    Ouvrez le formulaire quand vous etes pret.
                  </p>

                  <img
                    src="/images/systeme-location-performance/workflow-illustration.svg"
                    alt="Illustration du processus de candidature"
                    className="mt-6 h-48 w-full rounded-2xl border border-white/10 object-cover"
                    loading="lazy"
                  />

                  <button
                    type="button"
                    onClick={openFormModal}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-base font-semibold text-slate-950 transition duration-300 hover:-translate-y-1 hover:bg-blue-50"
                  >
                    Ouvrir le formulaire de candidature
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-slate-50 px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-4xl"
        >
          <SectionTitle
            eyebrow="FAQ"
            title="Les questions que se posent les propriétaires avant de se lancer"
            description="Des réponses simples pour comprendre comment cette offre s’intègre à votre quotidien et à votre activité."
          />

          <div className="mt-12 space-y-4">
            {faqItems.map((item) => (
              <details
                key={item.question}
                className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition duration-300 open:shadow-lg"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-lg font-semibold text-slate-950">
                  {item.question}
                  <ChevronDown className="h-5 w-5 shrink-0 text-slate-400 transition duration-300 group-open:rotate-180" />
                </summary>
                <div className="mt-4 max-w-3xl pr-6 text-base leading-7 text-slate-600">{item.answer}</div>
              </details>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="bg-white px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          variants={fadeInUp}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mx-auto max-w-6xl rounded-[36px] border border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 px-8 py-16 text-center text-white shadow-2xl shadow-slate-300/40 sm:px-12"
        >
          <h2 className="text-3xl font-semibold tracking-tight sm:text-5xl">
            Vous pouvez continuer comme aujourd’hui… ou passer au niveau supérieur
          </h2>
          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-300">
            Si vous voulez plus de réservations, moins de dispersion et un système qui tourne avec méthode, cette offre est conçue pour ça.
          </p>
          <button
            type="button"
            onClick={openFormModal}
            className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-blue-950/30 transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-950/30"
          >
            Postuler pour l'accompagnement
            <ArrowRight className="h-5 w-5" />
          </button>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-slate-50 px-6 py-10 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col items-center justify-between gap-4 text-center sm:flex-row sm:text-left">
            <div>
              <p className="text-sm font-semibold text-slate-900">Deleg-ai</p>
              <p className="mt-1 text-sm text-slate-500">Système Location Performance™</p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
              <a href="/mentions-legales-confidentialite/" className="transition hover:text-slate-950">
                Mentions légales
              </a>
              <a href="/conditions-generales-de-vente/" className="transition hover:text-slate-950">
                CGV
              </a>
              <a href="/mentions-legales-confidentialite/" className="transition hover:text-slate-950">
                Politique de confidentialité
              </a>
            </div>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-6 text-center text-xs leading-6 text-slate-500">
            Ce site ne fait pas partie du site Facebook™ ou de Facebook Inc. De plus, ce site n’est pas endossé par Facebook de quelque manière que ce soit.
          </div>

          <div className="mt-8 flex justify-center border-t border-slate-200 pt-6">
            <div ref={referralSlotRef} />
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {isFormOpen ? (
          <motion.div
            className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-950/75 px-4 py-8 backdrop-blur-sm sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFormModal}
          >
            <motion.div
              className="relative w-full max-w-4xl rounded-[30px] border border-white/10 bg-slate-950 p-6 text-white shadow-2xl shadow-blue-950/40 sm:p-8"
              initial={{ y: 28, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 20, scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.24, ease: 'easeOut' }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Fermer le formulaire"
                onClick={closeFormModal}
                className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-slate-100 transition hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="mb-8 pr-12">
                <h3 className="text-2xl font-semibold sm:text-3xl">Postulez pour l'accompagnement</h3>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                  Remplissez ce formulaire pour que nous evaluions votre situation et la meilleure facon de structurer votre systeme.
                </p>
              </div>

              <form noValidate onSubmit={handleSubmit} className="rounded-[24px] border border-white/10 bg-white/5 p-5 sm:p-6">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Nom</span>
                    <input name="nom" value={formData.nom} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Prenom</span>
                    <input name="prenom" value={formData.prenom} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Pays</span>
                    <input name="pays" value={formData.pays} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Numero de telephone</span>
                    <input name="telephone" value={formData.telephone} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Email</span>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400" />
                  </label>
                </div>

                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Profil</span>
                    <select name="profil" value={formData.profil} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400">
                      <option value="">Selectionner</option>
                      <option value="proprietaire_un_logement">Proprietaire d'un logement</option>
                      <option value="proprietaire_plusieurs_logements">Proprietaire de plusieurs logements</option>
                      <option value="societe_conciergerie">Societe de conciergerie</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Type de bien</span>
                    <input name="typeBien" value={formData.typeBien} onChange={handleChange} placeholder="Villa, appartement, chalet..." className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-blue-400" />
                  </label>
                </div>

                <label className="mt-5 block">
                  <span className="mb-2 block text-sm font-medium text-slate-200">Lien de votre annonce si existante</span>
                  <input
                    type="url"
                    name="lienAnnonce"
                    value={formData.lienAnnonce}
                    onChange={handleChange}
                    placeholder="https://www.airbnb.com/... ou https://www.booking.com/..."
                    className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 focus:border-blue-400"
                  />
                </label>

                <div className="mt-5">
                  <span className="mb-3 block text-sm font-medium text-slate-200">Situation actuelle</span>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {situationOptions.map((option) => (
                      <label key={option} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-slate-200">
                        <input type="checkbox" value={option} checked={formData.situationActuelle.includes(option)} onChange={handleSituationChange} className="h-4 w-4 rounded border-white/20 bg-transparent" />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {formData.situationActuelle.includes('Autre') ? (
                  <label className="mt-5 block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Precisez "Autre"</span>
                    <input name="autreSituation" value={formData.autreSituation} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400" />
                  </label>
                ) : null}

                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Utilisation d'un channel manager ?</span>
                    <select name="utiliseChannelManager" value={formData.utiliseChannelManager} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400">
                      <option value="">Selectionner</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-200">Si oui, lequel ?</span>
                    <input name="channelManagerNom" value={formData.channelManagerNom} onChange={handleChange} disabled={formData.utiliseChannelManager !== 'oui'} placeholder="Lodgify, Smoobu, Guesty..." className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition placeholder:text-slate-500 disabled:cursor-not-allowed disabled:opacity-50 focus:border-blue-400" />
                  </label>
                </div>

                <label className="mt-5 block">
                  <span className="mb-2 block text-sm font-medium text-slate-200">
                    Personne prete a suivre une methode etape par etape pour optimiser au maximum son bien ?
                  </span>
                  <select name="pretSuivreMethode" value={formData.pretSuivreMethode} onChange={handleChange} className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none transition focus:border-blue-400">
                    <option value="">Selectionner</option>
                    <option value="oui_totalement">Oui, totalement</option>
                    <option value="oui_avec_accompagnement">Oui, avec accompagnement</option>
                    <option value="non_pas_pour_le_moment">Non, pas pour le moment</option>
                  </select>
                </label>

                <label className="mt-5 flex items-start gap-3 rounded-2xl border border-white/10 bg-slate-900/60 px-4 py-4 text-sm leading-6 text-slate-300">
                  <input
                    type="checkbox"
                    name="acceptationLegale"
                    checked={formData.acceptationLegale}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-white/20 bg-transparent"
                  />
                  <span>
                    J’accepte la{' '}
                    <a href="/mentions-legales-confidentialite/" target="_blank" rel="noreferrer" className="font-medium text-blue-300 underline-offset-4 hover:underline">
                      politique de confidentialité
                    </a>{' '}
                    et les{' '}
                    <a href="/conditions-generales-de-vente/" target="_blank" rel="noreferrer" className="font-medium text-blue-300 underline-offset-4 hover:underline">
                      CGV
                    </a>
                    .
                  </span>
                </label>

                {status.message ? (
                  <div className={`mt-5 rounded-2xl px-4 py-3 text-sm ${status.type === 'success' ? 'bg-emerald-500/15 text-emerald-200' : 'bg-red-500/15 text-red-200'}`}>
                    {status.message}
                  </div>
                ) : null}

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeFormModal}
                    className="inline-flex items-center justify-center rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
                  >
                    Fermer
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3 text-sm font-semibold text-slate-950 transition duration-300 hover:-translate-y-0.5 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? 'Envoi en cours...' : "Envoyer ma candidature"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default SystemeLocationPerformancePage;

